import React from "react"
import moment from "moment"
import classnames from "classnames"

const LatestZones = ({ threads, user }) => {
  // Helper function to format time ago
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Unknown"
    return moment(dateString).fromNow()
  }

  // Helper function to get user initials
  const getUserInitials = (username) => {
    if (!username) return "?"
    return username.charAt(0).toUpperCase()
  }

  // Helper function to determine if thread has status flags
  const hasThreadFlags = (thread) => {
    return (
      thread.is_closed ||
      thread.is_hidden ||
      thread.is_unapproved ||
      thread.weight > 0 ||
      thread.best_answer ||
      thread.has_poll ||
      thread.has_unapproved_posts
    )
  }

  // Helper function to render thread status icons
  const renderThreadFlags = (thread) => {
    if (!hasThreadFlags(thread)) return null

    return (
      <ul className="thread-flags">
        {thread.weight == 2 && (
          <li
            className="thread-flag-pinned-globally"
            title="Pinned globally"
          >
            <span className="material-icon">bookmark</span>
          </li>
        )}
        {thread.weight == 1 && (
          <li
            className="thread-flag-pinned-locally"
            title="Pinned in category"
          >
            <span className="material-icon">bookmark_outline</span>
          </li>
        )}
        {thread.best_answer && (
          <li
            className="thread-flag-answered"
            title="Answered"
          >
            <span className="material-icon">check_circle</span>
          </li>
        )}
        {thread.has_poll && (
          <li className="thread-flag-poll" title="Poll">
            <span className="material-icon">poll</span>
          </li>
        )}
        {(thread.is_unapproved || thread.has_unapproved_posts) && (
          <li
            className="thread-flag-unapproved"
            title={thread.is_unapproved ? "Awaiting approval" : "Has unapproved posts"}
          >
            <span className="material-icon">visibility</span>
          </li>
        )}
        {thread.is_closed && (
          <li
            className="thread-flag-closed"
            title="Closed"
          >
            <span className="material-icon">lock</span>
          </li>
        )}
        {thread.is_hidden && (
          <li
            className="thread-flag-hidden"
            title="Hidden"
          >
            <span className="material-icon">visibility_off</span>
          </li>
        )}
      </ul>
    )
  }

  // Helper function to render category info (including subcategory)
  const renderCategoryInfo = (thread) => {
    const category = thread.category
    const parentCategory = thread.parentCategory

    return (
      <div className="threads-list-item-col-category">
        <div>
          {parentCategory && !parentCategory.special_role && (
            <a
              href={`/c/${parentCategory.slug}/${parentCategory.id}/`}
              className={`threads-list-item-category threads-list-category-label${parentCategory.color ? ' threads-list-category-label-color' : ''} threads-list-item-parent-category`}
              style={parentCategory.color ? { '--label-color': parentCategory.color } : {}}
              title={parentCategory.short_name ? parentCategory.name : undefined}
            >
              {parentCategory.short_name || parentCategory.name}
            </a>
          )}
          <a
            href={`/c/${category.slug}/${category.id}/`}
            className={`threads-list-item-category threads-list-category-label${category.color ? ' threads-list-category-label-color' : ''}`}
            style={category.color ? { '--label-color': category.color } : {}}
            title={category.short_name ? category.name : undefined}
          >
            {category.short_name || category.name}
          </a>
        </div>
      </div>
    )
  }

  // Process threads data
  const processedThreads = threads.map(thread => {
    const category = thread.category || {}
    const parentCategory = thread.parent_category || null
    
    return {
      id: thread.id,
      title: thread.title,
      slug: thread.slug,
      url: `/t/${thread.slug}/${thread.id}/`,
      author: {
        username: thread.starter?.username || "Unknown User",
        title: thread.starter?.title || "Member",
        avatar: thread.starter?.avatar || null
      },
      category: {
        id: category.id,
        name: category.name || "General",
        short_name: category.short_name,
        slug: category.slug,
        color: category.color || "#6b7280" // Use backend color or default
      },
      parentCategory: parentCategory ? {
        id: parentCategory.id,
        name: parentCategory.name,
        short_name: parentCategory.short_name,
        slug: parentCategory.slug,
        color: parentCategory.color || "#6b7280", // Use backend color or default
        special_role: parentCategory.special_role
      } : null,
      stats: {
        posts: thread.stats?.posts || thread.posts || 0,
        replies: thread.stats?.replies || thread.replies || 0,
        views: thread.stats?.views || 0
      },
      // Thread status flags
      weight: thread.weight || 0,
      is_closed: thread.is_closed || false,
      is_hidden: thread.is_hidden || false,
      is_unapproved: thread.is_unapproved || false,
      has_poll: thread.has_poll || false,
      best_answer: thread.best_answer || false,
      has_unapproved_posts: thread.has_unapproved_posts || false,
      timeAgo: formatTimeAgo(thread.last_post_on),
      createdAgo: formatTimeAgo(thread.created_on)
    }
  })

  if (processedThreads.length === 0) {
    return (
      <div className="latest-zones">
        <h2 className="section-title">Latest zones</h2>
        <div className="zones-empty">
          <p>No recent discussions available.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="latest-zones">
      <h2 className="section-title">Latest zones</h2>
      
      <div className="zones-list">
        {processedThreads.map((thread) => (
          <div key={thread.id} className="zone-item">
            <div className="zone-header">
            <div className="zone-avatar">
              <div className="avatar-placeholder">
                {getUserInitials(thread.author.username)}
              </div>
            </div>
            <div className="zone-meta">
                <span className="author-name">{thread.author.username}</span>
                <span className="author-title">{thread.author.title}</span>
                
              </div>
              <div className="zone-time">
                <span className="time-ago">{thread.createdAgo}</span>
              </div>
              </div>
            
            <div className="zone-content">
              
              
              <h3 className="zone-title">
                <a href={thread.url}>{thread.title}</a>
              
              </h3>
              <div className="tag-wrapper">
              <div className="zone-tags">
                {renderCategoryInfo(thread)}
              </div>
                {hasThreadFlags(thread) && (
                  <div className="zone-thread-flags">
                    {renderThreadFlags(thread)}
                  </div>
                )}
                </div>
              <div className="zone-wrapper">
              <div className="zone-stats">
                <span className="posts-count">
                  <i className="material-icon">chat_bubble_outline</i>
                  {thread.stats.posts} Posts
                </span>
                <span className="last-activity">
                  <i className="material-icon">schedule</i>
                  Last activity {thread.timeAgo}
                </span>
              </div>
               {/* <span className="material-icon">notifications_none</span> */}
               </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default LatestZones